// filterJsonSchema.js
/**
 * Campaign Filter Schema with Action-Based Support
 *
 * This implementation provides a seamless UX where:
 * 1. UI displays "Action Based" in dropdown and selected filters
 * 2. Backend query receives specific action types: ["take_a_survey", "open_app", "share_location"]
 *
 * How it works:
 * - User sees and selects "Action Based" in the Campaign Type dropdown
 * - UI continues to show "Action Based" in selected filters
 * - transformCampaignFilterData() runs only when sending query to backend
 * - Query receives: { subType: ["take_a_survey", "open_app", "share_location"] }
 *
 * Implementation:
 * - ListingComponentProvider uses filterTransform={transformCampaignFilterData}
 * - Transformation happens at query level, not UI level
 * - Maintains clean separation between UI display and backend data
 */
export const campaignFilterJsonSchema = (updatedCampaignConfiguration = []) => {
  // Filter out generic_campaign from the campaign options

  const campaignOptions = (updatedCampaignConfiguration || [])
    // .filter((campaign) => campaign.key !== "generic_campaign") // Removing for #19656701
    .map((campaign) => ({
      id: campaign.key,
      displayText: campaign.value?.label || campaign.name || campaign.key || "",
    }));

  return {
    type: "object",
    properties: {
      subType: {
        items: {
          type: "string",
          options: campaignOptions,
        },
      },
      eventDateBegin: {
        type: "string",
        format: "date",
        title: "Active Start Date",
      },
      eventDateEnd: {
        type: "string",
        format: "date",
        title: "Active End Date",
      },
    },
  };
};

export const campaignFilterUiSchema = {
  type: "HorizontalLayout",
  elements: [
    {
      type: "Control",
      scope: "#/properties/subType",
      component: "multipleSelectPerformanceList",
      selection: "multiple",
      placeholder: "Campaign Type",
      label: "Campaign Type",
    },
    {
      type: "Control",
      scope: "#/properties/eventDateBegin",
      component: "dateTimePicker",
      label: " Start Date",
    },
    {
      type: "Control",
      scope: "#/properties/eventDateEnd",
      component: "dateTimePicker",
      label: " End Date",
    },
  ],
};

/**
 * Transform filter data to handle action-based campaign types
 * @param {Object} filterData - The form filter data
 * @returns {Object} Transformed filter data
 */
export const transformCampaignFilterData = (filterData) => {
  if (!filterData || !filterData.subType) {
    return filterData;
  }

  // Create a copy of the filter data
  const transformedData = { ...filterData };

  // Check if subType contains action-based campaign type
  if (Array.isArray(filterData.subType)) {
    const hasActionBased = filterData.subType.some(
      (type) => type === "action-based" || type === "action_based"
    );

    if (hasActionBased) {
      // Replace action-based with the specific action types
      const otherTypes = filterData.subType.filter(
        (type) => type !== "action-based" && type !== "action_based"
      );
      transformedData.subType = [
        ...otherTypes,
        "take_a_survey",
        "open_app",
        "share_location",
      ];
    }
  } else if (
    filterData.subType === "action-based" ||
    filterData.subType === "action_based"
  ) {
    // Single selection case
    transformedData.subType = ["take_a_survey", "open_app", "share_location"];
  }

  return transformedData;
};

/**
 * Helper function to get the appropriate subType values based on campaign type
 * @param {string} campaignType - The selected campaign type
 * @returns {Array} Array of subType values
 */
export const getSubTypeValues = (campaignType) => {
  if (campaignType === "action-based" || campaignType === "action_based") {
    return ["take_a_survey", "open_app", "share_location"];
  }

  // For other campaign types, return the campaign type itself
  return [campaignType];
};

/**
 * Helper function to get action-based options for dropdown
 * @returns {Array} Array of action-based options
 */
export const getActionBasedOptions = () => [
  { id: "take_a_survey", displayText: "Take a Survey" },
  { id: "open_app", displayText: "Open App" },
  { id: "share_location", displayText: "Share Location" },
];

export const debounceFilterJsonSchema = {
  type: "object",
  properties: {
    name: {
      type: "string",
      title: "Search",
    },
  },
};

export const debounceFilterUiSchema = {
  type: "Control",
  scope: "#/properties/name",
  component: "searchInput",
  placeholder: "Search by name",
};

export const contentJsonSchema = {
  type: "object",
  properties: {
    subType: {
      type: "string",
      title: "Content Type",
      options: [
        { id: "page-content", displayText: "Content List" },
        { id: "banner-content", displayText: "Banners" },
        { id: "survey-quiz-content", displayText: "Surveys & Quizzes" },
      ],
    },
    entitySubType: {
      type: "string",
      title: "Content Sub Type",
      options: [
        { id: "hero", displayText: "Hero" },
        { id: "mini", displayText: "Mini" },
        { id: "survey", displayText: "Survey" },
        { id: "quiz", displayText: "Quiz" },
        { id: "image", displayText: "Image" },
        { id: "text", displayText: "Text" },
        { id: "video", displayText: "Video" },
      ],
    },
    eventDateBegin: {
      type: "string",
      format: "date",
      title: "Active Start Date",
    },
    eventDateEnd: {
      type: "string",
      format: "date",
      title: "Active End Date",
    },
  },
  required: [],
};

export const contentUiSchema = {
  type: "VerticalLayout",
  elements: [
    {
      type: "HorizontalLayout",
      elements: [
        {
          type: "Control",
          scope: "#/properties/subType",
          component: "multipleSelectPerformanceList",
          selection: "multiple",
          placeholder: "Content Type",
          label: "Content Type",
        },
        {
          type: "Control",
          scope: "#/properties/entitySubType",
          component: "multipleSelectPerformanceList",
          selection: "multiple",
          placeholder: "Sub Type",
          label: "Sub Type",
        },
        {
          type: "Control",
          scope: "#/properties/eventDateBegin",
          component: "dateTimePicker",
          label: " Start Date",
        },
        {
          type: "Control",
          scope: "#/properties/eventDateEnd",
          component: "dateTimePicker",
          label: " End Date",
        },
      ],
    },
  ],
};
